/**
 * Cover API service — Phase 3
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';

export interface Cover {
  id: string;
  publicUrl: string;
  variationIndex: number;
}

export function useGenerateCovers(bookId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiSuccess<{ covers: Cover[]; creditsDeducted: number }>>(
        `/books/${bookId}/covers`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['books', bookId] });
      void queryClient.invalidateQueries({ queryKey: ['covers', bookId] });
    },
  });
}

export function useCovers(bookId: string) {
  return useQuery({
    queryKey: ['covers', bookId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ covers: Cover[] }>>(`/books/${bookId}/covers`);
      return res.data.data.covers;
    },
    enabled: !!bookId,
  });
}

export function useSetPrimaryCover(bookId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (coverId: string) => {
      const res = await api.patch<ApiSuccess<{ updated: boolean }>>(
        `/books/${bookId}/covers/${coverId}/primary`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['books', bookId] });
    },
  });
}
